import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { Helmet } from "react-helmet-async";
import { 
  CheckCircle, 
  Phone, 
  Layout,
  FileText,
  MapPin,
  Image,
  Star,
  MessageSquare,
  Smartphone,
  ArrowRight
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const LandscapingWebsiteExamples = () => {
  const faqs = [
    {
      question: "What pages should a landscaping website have?",
      answer: "A complete landscaping website typically needs 7–14 pages: Home, About, Services (hub), individual service pages (patios, hardscaping, plantings, retaining walls), Project Gallery, Service Areas, Reviews, Contact, FAQ, and Privacy/Accessibility pages. The exact count depends on how many services and cities you offer."
    },
    {
      question: "How many service pages do I need for a landscaping site?",
      answer: "Create one dedicated page for each major service: patios, retaining walls, outdoor kitchens, plantings, landscape lighting, etc. Each page targets different keywords and gives potential customers exactly what they're searching for."
    },
    {
      question: "Should I put project pricing on my landscaping website?",
      answer: "You don't need exact prices, but giving ranges helps qualify leads. Visitors want to know if you're in their budget before requesting a consultation. Saying 'patio projects typically start at $8,000' is better than leaving them guessing."
    },
    {
      question: "How many project photos should I use in my gallery?",
      answer: "Aim for 15–30 photos organized by category (patios, walls, plantings, etc.). Include before/after pairs when possible. Quality matters more than quantity—10 stunning photos beat 50 mediocre ones."
    },
    {
      question: "What's the best quote form for landscaping projects?",
      answer: "Keep it to 4–5 fields: Name, Phone, City/ZIP, Project Type (dropdown), and optionally a brief project description. Every extra field reduces conversions. You can gather more details on the follow-up call."
    },
    {
      question: "Do service area pages look spammy for landscapers?",
      answer: "Only if you create thin, duplicate content. Each city page should have unique content—mention local landmarks, specific neighborhoods, and why you work in that area. Google rewards helpful, locally-relevant content."
    },
    {
      question: "How long should a landscaping service page be?",
      answer: "Aim for 500–1,000 words per service page. Include what the service is, the process, what's included, project types, pricing hints, FAQs, and a clear call-to-action. Long enough to be helpful, short enough to be scannable."
    },
    {
      question: "Should my landscaping site have a project portfolio or gallery?",
      answer: "Absolutely. Landscaping is visual—customers want to see your work before calling. Organize projects by type (patios, retaining walls, full outdoor living) and include before/after comparisons when possible."
    },
    {
      question: "How do I route different project types to the right form?",
      answer: "Use a 'Project Type' dropdown in your quote form with options like 'Patio/Hardscape,' 'Retaining Wall,' 'Plantings,' 'Full Landscape Design,' etc. This helps you prioritize leads and respond appropriately."
    },
    {
      question: "Should I use WordPress, Wix, or Squarespace for my landscaping site?",
      answer: "Any platform can work, but what matters more is speed, mobile responsiveness, and conversion-focused design. Our subscription sites are custom-built for landscaping businesses with all the features you need—no DIY learning curve required."
    }
  ];

  const exampleSections = [
    {
      title: "Homepage Hero + CTA Layout",
      icon: Layout,
      whatItIs: "The first thing visitors see—a bold headline, compelling subhead, trust signals, and prominent call-to-action buttons that drive consultations.",
      whyItConverts: [
        "States exactly what you do (design/build, hardscaping, etc.)",
        "Shows trust signals (years in business, projects completed)",
        "Makes it obvious how to request a consultation",
        "Works perfectly on mobile screens"
      ],
      whatWeNeed: [
        "Your business name and tagline",
        "Primary service area (city/region)",
        "One strong hero photo of your best project"
      ],
      altText: "Landscaping website hero section example with headline and call-to-action"
    },
    {
      title: "Services Overview Layout",
      icon: FileText,
      whatItIs: "A hub page that lists all your landscaping services with short descriptions and links to individual service pages.",
      whyItConverts: [
        "Helps visitors find the exact service they need",
        "Creates internal links that boost SEO",
        "Shows the full scope of design/build and maintenance",
        "Keeps navigation simple and intuitive"
      ],
      whatWeNeed: [
        "List of all services you offer",
        "1–2 sentence description for each",
        "Photos for each service (optional but helpful)"
      ],
      altText: "Landscaping website services overview page layout example"
    },
    {
      title: "Individual Service Page Example",
      icon: FileText,
      whatItIs: "A dedicated page for one service (like patio installation) with project examples, benefits, process overview, and a quote form.",
      whyItConverts: [
        "Targets specific search terms like 'patio installation [city]'",
        "Shows relevant project photos and before/afters",
        "Explains your process and what's included",
        "Has a focused call-to-action for that service"
      ],
      whatWeNeed: [
        "Service name and detailed description",
        "What's included in the service",
        "Photos of completed projects for that service"
      ],
      altText: "Patio installation service page example with project photos and quote form"
    },
    {
      title: "Service Area Page Example",
      icon: MapPin,
      whatItIs: "A page targeting a specific city or neighborhood, like 'Landscaping in [City Name].'",
      whyItConverts: [
        "Ranks for '[service] + [city]' searches",
        "Shows you actually work in that area",
        "Builds local relevance with Google",
        "Can mention local landmarks and neighborhoods"
      ],
      whatWeNeed: [
        "List of cities/areas you serve",
        "Any specific details about each area",
        "Photos from projects in those areas (ideal)"
      ],
      altText: "Landscaping service area page example for local SEO targeting"
    },
    {
      title: "Project Gallery / Portfolio Example",
      icon: Image,
      whatItIs: "A categorized gallery showcasing your completed projects—patios, retaining walls, plantings, full outdoor living transformations.",
      whyItConverts: [
        "Proves the quality of your work visually",
        "Organizes projects by type for easy browsing",
        "Before/after comparisons show transformation",
        "Helps visitors envision their own property"
      ],
      whatWeNeed: [
        "High-quality photos organized by project type",
        "Before and after shots when available",
        "Brief project descriptions (optional)"
      ],
      altText: "Landscaping portfolio gallery example with categorized projects"
    },
    {
      title: "Reviews & Testimonials Block",
      icon: Star,
      whatItIs: "A section displaying customer reviews, star ratings, and written testimonials from completed projects.",
      whyItConverts: [
        "Social proof from real customers",
        "Reduces hesitation for first-time visitors",
        "Google reviews add credibility",
        "Shows you deliver on high-ticket projects"
      ],
      whatWeNeed: [
        "Google review link or screenshots",
        "Any written testimonials you've received",
        "Customer first names and project types"
      ],
      altText: "Landscaping website reviews and testimonials section example"
    },
    {
      title: "Quote Form with Project Type Dropdown",
      icon: MessageSquare,
      whatItIs: "A simple form with Name, Phone, City, Project Type dropdown (patio, retaining wall, full design, etc.), and optional notes.",
      whyItConverts: [
        "Low friction—takes under a minute to complete",
        "Project type dropdown helps you prioritize leads",
        "Qualifies leads by service and location",
        "Works on mobile without scrolling"
      ],
      whatWeNeed: [
        "Email address to receive submissions",
        "Your preferred follow-up method",
        "List of project types for the dropdown"
      ],
      altText: "Landscaping quote form example with project type dropdown"
    },
    {
      title: "Mobile UX Example",
      icon: Smartphone,
      whatItIs: "How the site looks and functions on phones—tap-to-call, sticky buttons, and simple navigation for on-the-go visitors.",
      whyItConverts: [
        "60%+ of landscaping searches happen on mobile",
        "One-tap calling removes friction",
        "Sticky call button stays visible while scrolling",
        "Fast load times keep visitors engaged"
      ],
      whatWeNeed: [
        "Your phone number for click-to-call",
        "Confirmation of mobile responsiveness priority"
      ],
      altText: "Mobile landscaping website layout with tap-to-call button example"
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/landscaping-website-examples/#faq",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "Done-for-you websites for lawn care and landscaping companies that drive calls, quotes, and recurring clients."
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/landscaping-website-examples/#webpage",
    "name": "Landscaping Website Examples | Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com/landscaping-website-examples/",
    "description": "See landscaping website section examples that convert: hero, service pages, service areas, galleries, reviews, and quote forms for high-ticket leads.",
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    }
  };

  return (
    <>
      <SEO 
        title="Landscaping Website Examples"
        description="See landscaping website section examples that convert: hero, service pages, service areas, galleries, reviews, and quote forms for high-ticket leads."
        keywords="landscaping website examples, landscaping website layout, landscaping web design examples, hardscaping website examples, patio installation service page example"
        canonical="https://www.leafrushmarketing.com/landscaping-website-examples/"
        ogUrl="https://www.leafrushmarketing.com/landscaping-website-examples/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>

      <div className="flex flex-col">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="container px-4 md:px-6 relative z-10">
            <div className="mx-auto max-w-4xl text-center space-y-6">
              <Badge variant="secondary" className="mb-4">
                Website Section Examples
              </Badge>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Landscaping Website Examples That Convert
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                See the exact sections and layouts we use to build high-converting landscaping websites. Each example is designed to turn visitors into consultations and booked projects.
              </p>
              
              {/* Trust items */}
              <div className="flex flex-wrap gap-4 justify-center items-center text-sm font-medium max-w-2xl mx-auto pt-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  <span>$0 upfront</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  <span>$200–$300/mo</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  <span>Live in 10–14 days</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  <span>Month-to-month</span>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <a href="#get-started">
                  <Button size="lg" className="w-full sm:w-auto text-lg px-8">
                    <Phone className="mr-2 h-5 w-5" />
                    Get Started
                  </Button>
                </a>
                <NavLink to="/websites/">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8">
                    See Website Subscription Pricing
                  </Button>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* What You'll See */}
        <section className="py-12 bg-accent/20 border-y">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-6">
                What You'll See on This Page
              </h2>
              <ul className="space-y-3 max-w-xl mx-auto">
                <li className="flex gap-3 items-start">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                  <span>Homepage hero layouts that capture attention for design/build</span>
                </li>
                <li className="flex gap-3 items-start">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                  <span>Service page examples for patios, hardscaping, and outdoor living</span>
                </li>
                <li className="flex gap-3 items-start">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                  <span>Service area page structures for local SEO</span>
                </li>
                <li className="flex gap-3 items-start">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                  <span>Portfolio gallery layouts with categorized projects</span>
                </li>
                <li className="flex gap-3 items-start">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                  <span>Quote forms with project type routing</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Example Section Gallery */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-4xl font-bold mb-4">8 Landscaping Website Layout Examples</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                These are the typical sections and structures we build into every landscaping web design. Each example shows what works and why.
              </p>
            </div>

            <div className="space-y-12 max-w-5xl mx-auto">
              {exampleSections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <Card key={index} className="overflow-hidden">
                    <div className="grid md:grid-cols-2 gap-0">
                      {/* Visual Mock */}
                      <div className="bg-gradient-to-br from-primary/10 to-accent/30 p-8 flex items-center justify-center min-h-[250px]">
                        <div className="text-center space-y-4">
                          <div className="w-20 h-20 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto">
                            <Icon className="h-10 w-10 text-primary" />
                          </div>
                          <p className="text-sm text-muted-foreground font-medium">
                            {section.altText}
                          </p>
                        </div>
                      </div>

                      {/* Content */}
                      <CardContent className="p-6 md:p-8">
                        <h3 className="text-xl md:text-2xl font-bold mb-3">{section.title}</h3>
                        <p className="text-muted-foreground mb-4">{section.whatItIs}</p>
                        
                        <div className="space-y-4">
                          <div>
                            <p className="font-semibold text-sm mb-2">Why it converts:</p>
                            <ul className="space-y-1">
                              {section.whyItConverts.map((reason, i) => (
                                <li key={i} className="flex gap-2 text-sm text-muted-foreground">
                                  <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                                  <span>{reason}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div>
                            <p className="font-semibold text-sm mb-2">What we need from you:</p>
                            <ul className="space-y-1">
                              {section.whatWeNeed.map((item, i) => (
                                <li key={i} className="flex gap-2 text-sm text-muted-foreground">
                                  <ArrowRight className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                );
              })}
            </div>

            <div className="text-center mt-12">
              <p className="text-muted-foreground mb-4">
                Want all these sections built into your own site?
              </p>
              <NavLink to="/landscaping-website-design/">
                <Button variant="outline" size="lg">
                  See Our Landscaping Website Design Service
                </Button>
              </NavLink>
            </div>
          </div>
        </section>

        {/* Related Guides */}
        <section className="py-16 md:py-20 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Related Guides
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <NavLink to="/landscaping-website-design/">
                  <Card className="hover:border-primary transition-colors h-full">
                    <CardContent className="pt-6">
                      <p className="font-semibold mb-2">Landscaping Website Design</p>
                      <p className="text-sm text-muted-foreground">Our full service for landscaping businesses.</p>
                    </CardContent>
                  </Card>
                </NavLink>
                <NavLink to="/landscaping-website-cost/">
                  <Card className="hover:border-primary transition-colors h-full">
                    <CardContent className="pt-6">
                      <p className="font-semibold mb-2">Landscaping Website Cost</p>
                      <p className="text-sm text-muted-foreground">Compare pricing options and what you get.</p>
                    </CardContent>
                  </Card>
                </NavLink>
                <NavLink to="/service-pages-that-rank/">
                  <Card className="hover:border-primary transition-colors h-full">
                    <CardContent className="pt-6">
                      <p className="font-semibold mb-2">Service Pages That Rank</p>
                      <p className="text-sm text-muted-foreground">How to structure pages for SEO and conversions.</p>
                    </CardContent>
                  </Card>
                </NavLink>
                <NavLink to="/service-area-pages-local-seo/">
                  <Card className="hover:border-primary transition-colors h-full">
                    <CardContent className="pt-6">
                      <p className="font-semibold mb-2">Service Area Pages</p>
                      <p className="text-sm text-muted-foreground">Rank in nearby cities with local SEO.</p>
                    </CardContent>
                  </Card>
                </NavLink>
                <NavLink to="/growth-add-ons/gbp/">
                  <Card className="hover:border-primary transition-colors h-full">
                    <CardContent className="pt-6">
                      <p className="font-semibold mb-2">Google Business Profile</p>
                      <p className="text-sm text-muted-foreground">Optimize your GBP for more local visibility.</p>
                    </CardContent>
                  </Card>
                </NavLink>
                <NavLink to="/resources/">
                  <Card className="hover:border-primary transition-colors h-full">
                    <CardContent className="pt-6">
                      <p className="font-semibold mb-2">All Resources</p>
                      <p className="text-sm text-muted-foreground">Browse all our free guides and articles.</p>
                    </CardContent>
                  </Card>
                </NavLink>
              </div>
              
              {/* Cross-link to lawn care examples */}
              <div className="mt-8 p-4 bg-card rounded-lg border text-center">
                <p className="text-muted-foreground">
                  <strong>Also do lawn maintenance?</strong> See{" "}
                  <NavLink to="/lawn-care-website-examples/" className="text-primary hover:underline">
                    lawn care website examples
                  </NavLink>{" "}
                  for maintenance-focused layouts.
                </p>
              </div>
              
              <div className="text-center mt-6">
                <NavLink to="/contact/" className="text-primary hover:underline text-sm">
                  Have questions? Contact us →
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Common questions about landscaping website layouts and structure.
              </p>
            </div>

            <Accordion type="single" collapsible className="max-w-3xl mx-auto">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 md:py-20 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <h2 className="text-2xl md:text-4xl font-bold">
                Ready to Get a Site With All These Features?
              </h2>
              <p className="text-lg text-muted-foreground">
                Book a quick call. We'll learn about your business, show you what we can build, and give you a clear quote.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="#get-started">
                  <Button size="lg" className="w-full sm:w-auto text-lg px-8">
                    <Phone className="mr-2 h-5 w-5" />
                    Get Started
                  </Button>
                </a>
                <NavLink to="/contact/">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8">
                    Contact Us
                  </Button>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* Last Updated */}
        <div className="container px-4 md:px-6 pb-8">
          <p className="text-sm text-muted-foreground text-center">
            Last updated: December 19, 2024
          </p>
        </div>

        {/* Calendly Scheduling */}
        <CalendlyEmbed />
      </div>
    </>
  );
};

export default LandscapingWebsiteExamples;
